import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const Signup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    agreed: false
  });
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const generateSecurePassword = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let retVal = "";
    for (let i = 0; i < 14; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({ ...formData, password: retVal, confirmPassword: retVal });
  };

  const handleCopy = () => {
    if (formData.password) {
      navigator.clipboard.writeText(formData.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSignup = (e) => {
    e.preventDefault();
    setError('');

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!formData.agreed) {
      setError('You must agree to the Terms and Privacy Policy.');
      return;
    }

    navigate('/login');
  };

  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }} 
      animate={{ opacity: 1, x: 0 }} 
      transition={{ duration: 0.5 }} 
      className="w-full max-w-[400px] mx-auto"
    >
      <div className="text-left mb-8">
        <h5 className="text-[#11469A] text-xl font-bold mb-2">Create New Account</h5>
        <p className="text-[#878a99] text-sm">Join RM Ghana to start your career journey.</p>
      </div>

      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 text-xs font-bold rounded-lg flex items-center gap-2">
          <SafeIcon name="AlertCircle" size={16} />
          {error}
        </div>
      )}

      <form onSubmit={handleSignup} className="space-y-4">
        <div>
          <label className="block text-[12px] font-bold text-gray-500 uppercase tracking-wider mb-2">Email Address <span className="text-red-500">*</span></label>
          <input 
            type="email" 
            placeholder="name@company.com" 
            className="w-full px-4 py-3 rounded border border-[#ced4da] focus:border-[#00AEEF] focus:ring-2 focus:ring-[#00AEEF]/20 outline-none transition-all text-[13px]" 
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required 
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-[12px] font-bold text-gray-500 uppercase tracking-wider">Password <span className="text-red-500">*</span></label>
            <button 
              type="button" 
              onClick={generateSecurePassword}
              className="text-[11px] font-bold text-[#00AEEF] hover:underline"
            >
              Generate Secure Password
            </button>
          </div>
          <div className="relative">
            <input 
              type={showPassword ? "text" : "password"} 
              placeholder="Min. 8 characters" 
              className="w-full px-4 py-3 rounded border border-[#ced4da] focus:border-[#00AEEF] focus:ring-2 focus:ring-[#00AEEF]/20 outline-none transition-all text-[13px] pr-20" 
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required 
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-2">
              <button type="button" onClick={handleCopy} className="text-[#878a99] hover:text-[#00AEEF]">
                <SafeIcon name={copied ? "Check" : "Copy"} size={16} />
              </button>
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#878a99] hover:text-[#495057]">
                <SafeIcon name={showPassword ? "EyeOff" : "Eye"} size={16} />
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[12px] font-bold text-gray-500 uppercase tracking-wider mb-2">Confirm Password <span className="text-red-500">*</span></label>
          <div className="relative">
            <input 
              type={showConfirmPassword ? "text" : "password"} 
              placeholder="Repeat password" 
              className="w-full px-4 py-3 rounded border border-[#ced4da] focus:border-[#00AEEF] focus:ring-2 focus:ring-[#00AEEF]/20 outline-none transition-all text-[13px] pr-10" 
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required 
            />
            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#878a99] hover:text-[#495057]">
              <SafeIcon name={showConfirmPassword ? "EyeOff" : "Eye"} size={16} />
            </button>
          </div>
        </div>

        <div className="flex items-start gap-2 pt-2">
          <input 
            type="checkbox" 
            id="terms"
            className="mt-1 h-4 w-4 rounded border-gray-300 text-[#00AEEF] focus:ring-[#00AEEF]"
            checked={formData.agreed}
            onChange={(e) => setFormData({ ...formData, agreed: e.target.checked })}
          />
          <label htmlFor="terms" className="text-[13px] text-gray-500 leading-tight">
            I agree to the <Link to="/terms" className="text-[#00AEEF] font-semibold hover:underline">Terms of Use</Link> and <Link to="/privacy" className="text-[#00AEEF] font-semibold hover:underline">Privacy Policy</Link>
          </label>
        </div>

        <button 
          type="submit" 
          className="w-full bg-[#00AEEF] hover:bg-[#009bd6] text-white py-3.5 rounded font-bold transition-all shadow-md mt-4 text-[13px] uppercase tracking-wider"
        >
          Create Account
        </button>
      </form>

      <div className="mt-8 text-center sm:text-left">
        <p className="text-[13px] text-[#878a99]">
          Already have an account?{' '}
          <Link to="/login" className="text-[#00AEEF] font-bold hover:underline decoration-2 underline-offset-4">
            Sign In
          </Link>
        </p>
      </div>
    </motion.div>
  );
};

export default Signup;